/**
 * Sélecteur de membre d'équipe (remplace un <select> HTML classique).
 *
 * Bouton affichant l'avatar + le pseudo du membre choisi (ou un libellé
 * "vide"), qui ouvre un panneau listant les membres disponibles avec leur
 * avatar — utilisé pour désigner le lieutenant, affecter les postes de la
 * composition, ou remplir une feuille de match.
 */

import { useState, useEffect, useRef } from 'react';
import { PlayerAvatar } from '../PlayerAvatar';

export interface MemberSelectOption {
  pseudo: string;
  pseudo_display?: string;
}

interface MemberSelectProps {
  value: string;
  onChange: (pseudo: string) => void;
  options: MemberSelectOption[];
  emptyLabel?: string; // Libellé de l'option "vide" (ex. "Aucun", "Vacant")
  allowEmpty?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export default function MemberSelect({
  value, onChange, options, emptyLabel = 'Aucun', allowEmpty = true, disabled, placeholder,
}: MemberSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selected = options.find((o) => o.pseudo === value) || null;

  return (
    <div ref={rootRef} className="tm-select">
      <button
        type="button"
        className="tm-select-trigger"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? (
          <PlayerAvatar pseudo={selected.pseudo} size={22} />
        ) : null}
        <span className={`tm-select-trigger-label ${selected ? 'filled' : ''}`}>
          {selected ? (selected.pseudo_display || selected.pseudo) : (placeholder || emptyLabel)}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>▾</span>
      </button>

      {open && (
        <div className="tm-select-panel">
          {allowEmpty && (
            <button
              type="button"
              className={`tm-select-option empty ${!value ? 'selected' : ''}`}
              onClick={() => { onChange(''); setOpen(false); }}
            >
              {emptyLabel}
            </button>
          )}
          {options.map((o) => (
            <button
              key={o.pseudo}
              type="button"
              className={`tm-select-option ${o.pseudo === value ? 'selected' : ''}`}
              onClick={() => { onChange(o.pseudo); setOpen(false); }}
            >
              <PlayerAvatar pseudo={o.pseudo} size={22} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.pseudo_display || o.pseudo}
              </span>
            </button>
          ))}
          {options.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Aucun membre disponible
            </div>
          )}
        </div>
      )}
    </div>
  );
}
